import { cn } from '@/utils/functions';
import { Button, Text, toast, Tooltip } from '@bsf/force-ui';
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { ArrowUpRight, Check, Info } from 'lucide-react';
import apiFetch from '@wordpress/api-fetch';
import useStore, { actionTypes } from '@/store';

const ActionItemStep = ( { step, isLast = false, actionItem } ) => {
	const { id, title, cta, completed = false, content = [] } = step;

	const { dispatch, actionItems } = useStore( ( state ) => ( {
		dispatch: state.dispatch,
		actionItems: state.actionItems,
	} ) );
	const [ isCompleted, setIsCompleted ] = useState( completed );

	// Update local state when completed prop changes
	useEffect( () => {
		setIsCompleted( completed );
	}, [ completed ] );

	if ( ! id || ! title ) {
		return null;
	}

	const handleCompletion = () => {
		const newStatus = ! isCompleted;
		const newActionItems = actionItems.map( ( item ) => {
			if ( item.id === actionItem.id ) {
				return {
					...item,
					steps: item?.steps?.map( ( STEP ) => {
						if ( STEP?.id === id ) {
							return {
								...STEP,
								completed: newStatus,
							};
						}
						return STEP;
					} ),
				};
			}
			return item;
		} );

		// Optimistic UI update
		setIsCompleted( newStatus );
		dispatch( {
			type: actionTypes.SET_ACTION_ITEMS,
			payload: newActionItems,
		} );

		const handleError = ( errTitle, message ) => {
			const previousStatus = isCompleted;
			const previousActionItem = actionItems;

			// Revert optimistic UI update on failure and error
			setIsCompleted( previousStatus );
			dispatch( {
				type: actionTypes.SET_ACTION_ITEMS,
				payload: previousActionItem,
			} );

			toast.error( errTitle, {
				description:
					message ||
					__(
						'An error occurred while updating the step status.',
						'getting-started'
					),
			} );
		};

		apiFetch( {
			path: 'getting-started/v1/update-action-item-steps/',
			method: 'POST',
			data: {
				action_id: actionItem?.id,
				steps: newActionItems.find(
					( item ) => item.id === actionItem.id
				)?.steps,
			},
			headers: {
				'X-WP-Nonce': gettingStartedVars.rest_api_nonce,
			},
		} )
			.then( ( response ) => {
				if ( ! response.success ) {
					handleError(
						__(
							'Failed to update step status:',
							'getting-started'
						),
						response?.data?.message
					);
				}
			} )
			.catch( ( error ) => {
				handleError(
					__( 'Error updating step status:', 'getting-started' ),
					error?.message
				);
			} );
	};

	const handleLearnMoreClick = () => {
		if ( step?.content?.type === 'link' ) {
			window.open( step?.content?.url, '_blank' );
			return;
		}

		dispatch( {
			type: actionTypes.SET_LEARN_MORE_DIALOG_ITEM,
			payload: {
				open: true,
				item: step,
			},
		} );
	};

	const handleActionClick = () => {
		if ( cta?.url ) {
			window.open( cta.url, cta?.target || '_blank' );
		}
	};

	const statusText = isCompleted
		? __( 'Mark as incomplete', 'getting-started' )
		: __( 'Mark as complete', 'getting-started' );

	return (
		<div
			className={ cn(
				'py-4 sm:py-5 flex items-center gap-2 sm:gap-3',
				isLast ? '' : 'border-b-0.5 border-solid border-border-subtle'
			) }
		>
			<Tooltip
				arrow
				content={ statusText }
				placement="top"
				variant="dark"
			>
				<span
					className={ cn(
						'rounded-full cursor-pointer border-[1.25px] border-solid',
						isCompleted
							? 'bg-support-success p-[3px] sm:p-[4px] border-support-success-inverse'
							: 'flex justify-center items-center w-5 h-5 sm:w-6 sm:h-6 border-border-strong [&:hover>svg]:text-border-strong'
					) }
					tabIndex={ 0 }
					aria-label={ statusText }
					onClick={ handleCompletion }
				>
					<Check
						className="text-icon-on-color sm:w-3.5 sm:h-3.5 transition-all duration-200"
						size={ 12 }
						strokeWidth={ 1.5 }
					/>
				</span>
			</Tooltip>

			<Text
				className="flex-1 sm:text-base"
				size={ 14 }
				weight={ 500 }
				color="primary"
			>
				{ title }
			</Text>

			{ ( content?.length || content?.type === 'link' ) && (
				<Button
					size="xs"
					variant="ghost"
					icon={ <Info size={ 14 } className="inline sm:hidden" /> }
					onClick={ handleLearnMoreClick }
					className="outline-none hover:bg-transparent underline-offset-3 hover:underline hover:text-gs-primary focus:ring-0 focus:ring-offset-0"
				>
					<span className="hidden sm:inline">
						{ content?.title ||
							__( 'Learn how', 'getting-started' ) }
					</span>
				</Button>
			) }

			<Button
				className="px-3 sm:px-5 gap-0.5 text-gs-primary outline-gs-primary hover:text-button-tertiary hover:outline-gs-primary hover:bg-gs-primary focus:ring-gs-primary w-[104px]"
				size="sm"
				variant="outline"
				icon={ <ArrowUpRight size={ 14 } /> }
				iconPosition="right"
				onClick={ handleActionClick }
			>
				{ cta?.label || __( 'Set Up', 'getting-started' ) }
			</Button>
		</div>
	);
};

export default ActionItemStep;
